<template>
    <div class="AdminEntityItemEdit">
        <FadeTransition mode="out-in">
            <div class="AdminEntityItemHeader flex row-stretch-center">
                <Breadcrumbs :breadcrumbs="breadcrumbs" />
                <div v-if="buttons">
                    <WButton
                        v-for="({ text, disabled, onClick, ...otherProps }, index) in buttons"
                        :key="index"
                        :disabled="disabled"
                        v-bind="otherProps"
                        class="ml-1"
                        @click="onClick"
                    >
                        {{ text }}
                    </WButton>
                </div>
            </div>
        </FadeTransition>

        <WCard :loading="isLoading">
            <EntityItemForm
                v-model="row"
                :columns="columns"
                :selectGroups="selectGroups"
                :entityKey="entityKey"
            />
        </WCard>

        <ModalRequest
            :opened="modal === modals.DELETE"
            :title="$t('entities.actionsModals.deleteSelection.text')"
            :cancelText="$t('actions.cancel')"
            :okText="$t('actions.confirm')"
            :loading="isRemoving"
            preventDefault
            @close="handleCleanModal"
            @onAccept="removeItem"
        />
    </div>
</template>

<script>
import {
    Api, apiRequest, rethrowError, notifyHttpError, notifySuccess, notifyError,
} from '@/api'
import { Breadcrumbs, ModalRequest } from '@/components'
import { EntityItemForm } from '../components'

const MODAL_TYPES = {
    DELETE: 'DELETE',
}

export default {
    name: 'AdminEntityItemEdit',
    components: {
        Breadcrumbs,
        ModalRequest,
        EntityItemForm,
    },
    metaInfo () {
        return {
            title: this.$t(`admin.navigation.entities.${this.entityKey}`),
        }
    },
    data () {
        return {
            meta: undefined,
            row: undefined,
            columns: undefined,
            selectGroups: undefined,
            isLoading: false,

            // Modals
            modal: undefined,
            modals: MODAL_TYPES,
            isRemoving: false,
        }
    },
    computed: {
        breadcrumbs () {
            return [{
                key: this.entityKey,
                label: this.$t(`admin.navigation.entities.${this.entityKey}`),
                to: { name: 'AdminEntityList', params: { entityKey: this.entityKey } },
            }, {
                key: 'entity-detail',
                label: (this.row || {})[(this.meta || {}).nameField] || '',
            }]
        },
        buttons () {
            return [
                {
                    iconLeft: 'trash',
                    text: this.$t('entities.actions.delete'),
                    outline: true,
                    onClick: () => this.handleRequestModal(this.modals.DELETE),
                }, {
                    iconLeft: 'save',
                    text: this.$t('entities.actions.save'),
                    loading: this.isLoading,
                    disabled: this.isLoading,
                    onClick: this.editRow,
                }]
        },
    },
    beforeCreate () {
        const { entityKey, id } = this.$route.params
        this.entityKey = entityKey
        this.id = id
    },
    mounted () {
        this.getData()
    },
    methods: {
        handleRequestModal (modal) {
            this.modal = modal
        },
        handleCleanModal () {
            this.modal = undefined
        },
        async getData () {
            this.isLoading = true
            apiRequest(Api().outputs.get({ entityKey: this.entityKey, params: { id: this.id } }))
                .then(({ meta, data }) => {
                    const { selectGroups, columns, rows } = data || {}
                    const [row] = rows || []
                    this.meta = meta
                    this.row = row
                    this.selectGroups = selectGroups
                    this.columns = columns
                })
                .catch((error) => {
                    this.$router.back()
                    rethrowError(notifyHttpError())(error)
                })
                .finally(() => (this.isLoading = false))
        },
        async editRow () {
            this.isLoading = true
            const params = { type: this.entityKey, row: this.row }
            apiRequest(Api().imports.create(params))
                .then(({ data }) => {
                    notifySuccess(this.$t('entities.modals.editSuccess'))()
                    this.$router.push({ name: 'AdminEntityItemDetail', params: { entityKey: this.entityKey, id: this.id } })
                })
                .catch(rethrowError(notifyHttpError()))
                .finally(() => (this.isLoading = false))
        },
        async removeItem () {
            this.isRemoving = true
            apiRequest(Api().outputs.delete({ entityKey: this.entityKey, id: this.id }))
                .then((response) => {
                    notifySuccess(this.$t('entities.modals.removeSuccess'))()
                    this.$router.push({ name: 'AdminEntityList', params: { entityKey: this.entityKey } })
                })
                .catch(rethrowError(notifyError()))
                .finally(() => {
                    this.handleCleanModal()
                    this.isRemoving = false
                })
        },
    },
}
</script>
<style lang="scss" scoped>
.AdminEntityItemHeader {
    margin-bottom: 20px;
}

.WCard {
	@include admin-screen-height(54px);
}
</style>
